import PropTypes from 'prop-types';

import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import useEnv from '@zenbusiness/application-commons-env';
import Box from '@zenbusiness/zui/Box';
import Card from '@zenbusiness/zui/Card';
import CardHeader from '@zenbusiness/zui/CardHeader';
import Chip from '@zenbusiness/zui/Chip';
import ErrorState from '@zenbusiness/zui/ErrorState';
import Link from '@zenbusiness/zui/Link';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import { useTheme } from '@zenbusiness/zui/styles';
import Tooltip from '@zenbusiness/zui/Tooltip';

import useActionTakenUpdate from '#client/hooks/useActionTakenUpdate';
import { generateZenUrl } from '#client/utils/urls';

import * as Styled from './NextUpCard.style';
import { formatEstimate } from '../../utils/formatEstimate';
import { TooltipTitle } from '../TooltipTitle';

function NextUpCard({
  ctaType,
  ctaUrl,
  description,
  estimate,
  highlight,
  imageUrl,
  instanceUuid,
  onClick,
  title,
  tooltip,
  readinessStatus
}) {
  const { DEPLOY_ENV } = useEnv();
  const { palette } = useTheme();

  const {
    saveActionTaken,
    hasError: actionTakenError
  } = useActionTakenUpdate(instanceUuid);

  const linkPressedCallback = useLinkPressedCallback({
    appSection: 'home',
    destination: ctaUrl,
    elementName: 'next_up_card',
    elementText: title
  });

  if (actionTakenError) {
    return (
      <ErrorState eventProps={{
        appSection: 'home'
      }}
      />
    );
  }

  const handleOnClick = () => {
    linkPressedCallback();
    onClick?.();
    saveActionTaken();
  };

  // Note: Generate a fully qualified URL so the MUI Link component is used by the Card.
  // This is not ideal but ensures the various external paths passed a url's can be opened.
  // In the future, we may be able match the path using matchPath and use the Link component.
  const fullUrl = ctaUrl.startsWith('http') ? ctaUrl : generateZenUrl(ctaUrl);
  const devBackground = readinessStatus === 'development' && DEPLOY_ENV !== 'production';
  const linkProps = ctaType === 'modal' ? { to: ctaUrl } : { href: fullUrl };

  return (
    <Card
      interactive
      component={Link}
      aria-label={title}
      onClick={handleOnClick}
      target={ctaType === 'external' ? '_blank' : '_self'}
      sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      {...linkProps}
    >
      <Styled.NextUpCardContent
        highlight={highlight}
        devbackground={devBackground}
      >
        <Styled.CarrdContainer gap={2}>
          <Styled.LogoContainer>
            <SharedAsset alt="" src={imageUrl} />
          </Styled.LogoContainer>

          <Styled.Content gap={2}>
            <CardHeader subtitle={description} title={title} />
            <Box>
              <Chip
                icon={(
                  <SharedAsset
                    color={highlight ? palette.fixed.white : palette.fixed.black}
                    alt="clock-icon"
                    src="/s2/icon/clock-fill.svg"
                    width={16}
                  />
              )}
                size="sm"
                sx={{
                  backgroundColor: highlight && palette.fixed.black,
                  color: highlight && palette.fixed.white
                }}
              >
                {formatEstimate(estimate)}
              </Chip>
            </Box>
          </Styled.Content>
        </Styled.CarrdContainer>

        {!!tooltip && (
          <Tooltip
            arrow={false}
            componentsProps={{
              tooltip: {
                sx: {
                  borderRadius: '0.75rem',
                  padding: '0'
                }
              }
            }}
            enterTouchDelay={0}
            leaveTouchDelay={15000}
            title={<TooltipTitle text={tooltip} />}
            placement="bottom-start"
            sx={{
              position: 'absolute',
              top: '1.5rem',
              right: '1.5rem'
            }}
          >
            <SharedAsset
              alt="info"
              color={palette.info.light}
              src="/s2/icon/question-mark-outline.svg"
              width={24}
            />
          </Tooltip>
        )}
      </Styled.NextUpCardContent>
    </Card>
  );
}

NextUpCard.propTypes = {
  ctaType: PropTypes.string,
  ctaUrl: PropTypes.string,
  description: PropTypes.string,
  estimate: PropTypes.string,
  highlight: PropTypes.bool,
  imageUrl: PropTypes.string,
  instanceUuid: PropTypes.string,
  onClick: PropTypes.func,
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  readinessStatus: PropTypes.string
};

export default NextUpCard;
